import React from 'react';
import { View, Text, StyleSheet, Image, TouchableOpacity, Platform } from 'react-native';
import { MdLocationOn, MdPhone, MdSchedule, MdShoppingBag, MdDirectionsTransit } from 'react-icons/md';
import ItineraryItemCard from './ItineraryItemCard';
import { ORANGE_ACTION } from '../../constants/colors';

export interface ShoppingCardProps {
  title: string;
  note?: string;
  venue?: string;
  category?: string;
  priceRange?: string;
  address?: string;
  phone?: string;
  openingHours?: Record<string, string> | string;
  transportNote?: string;
  actionIcon?: React.ReactNode;
  imageUrl?: string;
}

const formatOpeningHours = (hours: Record<string, string> | string | undefined): string | undefined => {
  if (!hours) return undefined;
  if (typeof hours === 'string') return hours.trim() || undefined;
  if (typeof hours === 'object') {
    const entries = Object.entries(hours).filter(([_, value]) => value && typeof value === 'string' && value.trim());
    if (entries.length === 0) return undefined;
    return entries.map(([day, time]) => `${day} ${time}`).join('\n');
  }
  return undefined;
};

const ShoppingCard: React.FC<ShoppingCardProps> = ({ 
  title, 
  note, 
  address, 
  phone, 
  openingHours, 
  transportNote,
  actionIcon,
  imageUrl,
}) => {
  const formattedOpeningHours = formatOpeningHours(openingHours);
  const [noteExpanded, setNoteExpanded] = React.useState(false);
  const shouldShowToggle = (() => {
    if (!note) return false;
    try {
      const len = String(note).length;
      if (len >= 80) return true;
      if (String(note).includes('\n')) return true;
      if ((String(note).match(/\s+/g) || []).length > 12) return true;
      return false;
    } catch {
      return false;
    }
  })();
  const cardBody = (
    <>
      <View style={styles.rowTop}>
        <Text style={styles.title}>{title}</Text>
        <View style={styles.actions}>
          {actionIcon ? actionIcon : React.createElement(MdShoppingBag as any, { size: 16, color: ORANGE_ACTION })}
        </View>
      </View>
      
      {address ? (
        <View style={styles.infoRow}>
          <View style={styles.iconWrapper}>
            {React.createElement(MdLocationOn as any, { size: 16, color: '#9CA3AF' })}
          </View>
          <Text style={styles.infoText} numberOfLines={2}>{address}</Text>
        </View>
      ) : null}
      
      {phone ? (
        <View style={styles.infoRow}>
          <View style={styles.iconWrapper}>
            {React.createElement(MdPhone as any, { size: 16, color: '#9CA3AF' })}
          </View>
          <Text style={styles.infoText}>{phone}</Text>
        </View>
      ) : null}

      {transportNote ? (
        <View style={styles.infoRow}>
          <View style={styles.iconWrapper}>
            {React.createElement(MdDirectionsTransit as any, { size: 16, color: '#9CA3AF' })}
          </View>
          <Text style={styles.infoText} numberOfLines={3}>{transportNote}</Text>
        </View>
      ) : null}
      
      {formattedOpeningHours ? (
        <View style={styles.infoRow}>
          <View style={styles.iconWrapper}>
            {React.createElement(MdSchedule as any, { size: 16, color: '#9CA3AF' })}
          </View>
          <Text style={styles.infoText} numberOfLines={3}>
            {formattedOpeningHours}
          </Text>
        </View>
      ) : null}

      {note ? (
        <>
          <Text style={styles.noteText} numberOfLines={noteExpanded ? undefined : 2}>{note}</Text>
          {shouldShowToggle ? (
            <TouchableOpacity style={styles.noteToggleButton} onPress={() => setNoteExpanded(v => !v)}>
              <Text style={styles.noteToggleText}>{noteExpanded ? '收起 ▴' : '展开 ▾'}</Text>
            </TouchableOpacity>
          ) : null}
        </>
      ) : null}
    </>
  );

  if (imageUrl) {
    return (
      <View style={styles.cardContainer}>
        <View style={styles.imageWrapper}>
          <Image source={{ uri: imageUrl }} style={styles.image} resizeMode="cover" />
        </View>
        <View style={styles.cardContent}>
          {cardBody}
        </View>
      </View>
    );
  }

  return (
    <ItineraryItemCard>
      {cardBody}
    </ItineraryItemCard>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    overflow: 'hidden',
    // Use boxShadow on web; keep native shadow props for iOS/Android
    ...Platform.select({
      web: {
        boxShadow: '0px 8px 20px rgba(0,0,0,0.06)'
      },
      default: {
        shadowColor: '#000',
        shadowOpacity: 0.06,
        shadowOffset: { width: 0, height: 8 },
        shadowRadius: 20,
        elevation: 2,
      },
    }),
  },
  imageWrapper: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#F3F4F6',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  cardContent: {
    paddingVertical: 14,
    paddingHorizontal: 12,
    backgroundColor: '#fff',
  },
  rowTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
    marginRight: 8,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  noteText: {
    fontSize: 14,
    color: '#4B5563',
    lineHeight: 20,
    marginTop: 12,
    marginBottom: 8,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  iconWrapper: {
    marginRight: 8,
    marginTop: 2,
  },
  infoText: {
    fontSize: 14,
    color: '#6B7280',
    flex: 1,
    lineHeight: 20,
  },
  noteToggleButton: { marginTop: 8, paddingVertical: 10, borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  noteToggleText: { fontSize: 14, color: ORANGE_ACTION },
});

export default ShoppingCard;